import type React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ConfigProvider, Popover, Tabs, Tooltip, Typography } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { usePermissionCheck } from "@components/PermissionedComponents";
import { useDataPipeline } from "../DataPipeline";
import { useEditController, useWidgetData } from "../Contexts/dashboardHooks";
import { useControllerHover } from "../Contexts/ControllerHoverContext";
import type {
  ControllerKind,
  DynamicControllerBuilderMisc,
  DynamicControllerComponentSpec,
} from "../Processing/types";
import { controllerBareId, faintSliceColor, sliceColor } from "../Processing/controllerBindings";
import { controllerSummary, controllerSummaryDetailed } from "../AddWidgetModal/shared/SectionSummary";
import { DateRangeForm, DATE_RANGE_DEFAULTS } from "../AddWidgetModal/shared/DateRangeForm";
import { PlayersForm, PLAYERS_DEFAULTS } from "../AddWidgetModal/shared/PlayersForm";
import { SingleDateForm, SINGLE_DATE_DEFAULTS } from "../AddWidgetModal/shared/SingleDateForm";
import { SinglePlayerForm, SINGLE_PLAYER_DEFAULTS } from "../AddWidgetModal/shared/SinglePlayerForm";

const { Text } = Typography;

// The smallest tile the inline pickers survive in at antd's default control height:
// 2 columns (≈ 226px) by 3 rows (≈ 110px) — the tile chrome is trimmed so two stacked
// 32px selects fit a 3-row tile. Below either edge the tile renders the chip face and
// the pickers move into the hover popover.
const INLINE_MIN_WIDTH_PX = 200;
const INLINE_MIN_HEIGHT_PX = 100;

// A chip tile at 2 grid rows (~70px) has room for a second text line, so it shows the
// detailed summary (athlete and drill names) instead of "N selected". A 1-row tile
// (~30px) keeps the one-line chip. The threshold sits safely between the two.
const TALL_CHIP_MIN_HEIGHT_PX = 56;

// A change with no dropdown open (day-filter tags, the custom-range checkbox) commits after
// this pause. Changes made inside an open dropdown wait for the dropdown to close instead.
const IDLE_COMMIT_MS = 1500;

/**
 * The dashboard-surface pickers. Moving one is an ordinary widget edit: it rewrites the
 * controller's spec, saves through the normal autosave path, and refetches the dashboard,
 * so every widget that follows this controller picks the change up. A user without edit
 * permission sees the current selection but cannot change it.
 *
 * Picks accumulate in a local draft and commit as ONE edit when the coach is done: the
 * dropdown closes, the chip popover closes, the pointer or focus leaves the tile with no
 * dropdown open, or a short idle pause passes. Without the draft, every pill in a
 * multi-select saved and refetched the dashboard on its own.
 *
 * The tile has two faces. At or above the old minimum size it renders the pickers inline.
 * Smaller than that — down to spacer size — it renders a one-line chip (title + current
 * values), and the pickers open in a popover on hover or click.
 */
export function DynamicControllerWidget({ id }: { id: string }) {
  const processed = useWidgetData(id);
  const pipeline = useDataPipeline();
  const { permissionDisabled: cannotEdit } = usePermissionCheck("EditSandboxBoards");
  const controllerUuid = controllerBareId(id);
  const editController = useEditController(controllerUuid);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Pointing at the slicer outlines every widget that follows it (see GridItemFrame).
  const { hover, unhover } = useControllerHover();
  useEffect(() => () => unhover(controllerUuid), [unhover, controllerUuid]);

  const spec =
    processed?.type === "slicer"
      ? (processed.spec as DynamicControllerComponentSpec)
      : undefined;

  const savedMisc = spec?.slicer_builder_misc;

  // ── Draft: what the pickers show; `savedMisc` is what the dashboard has committed. ──
  const [draft, setDraft] = useState<Partial<DynamicControllerBuilderMisc> | null>(null);
  const draftRef = useRef(draft);
  const specRef = useRef(spec);
  specRef.current = spec;
  const openPopupsRef = useRef(0);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(idleTimerRef.current), []);

  const misc = useMemo(
    () => (draft && savedMisc ? { ...savedMisc, ...draft } : savedMisc),
    [savedMisc, draft],
  );

  const commit = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    const pending = draftRef.current;
    const current = specRef.current;
    if (!pending || !current) return;
    draftRef.current = null;
    setDraft(null);
    editController({
      ...current,
      slicer_builder_misc: { ...current.slicer_builder_misc, ...pending },
    });
  }, [editController]);

  const commitWhenNoPopupOpen = useCallback(() => {
    if (openPopupsRef.current === 0) commit();
  }, [commit]);

  const applyPatch = useCallback(
    (patch: Partial<DynamicControllerBuilderMisc>) => {
      const next = { ...(draftRef.current ?? {}), ...patch };
      draftRef.current = next;
      setDraft(next);
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(commitWhenNoPopupOpen, IDLE_COMMIT_MS);
    },
    [commitWhenNoPopupOpen],
  );

  // Dropdowns, date pickers and the custom-range popover report open/close. A close with
  // nothing else open is the coach's "done" signal.
  const onPopupOpenChange = useCallback(
    (open: boolean) => {
      openPopupsRef.current = Math.max(0, openPopupsRef.current + (open ? 1 : -1));
      if (open) clearTimeout(idleTimerRef.current);
      else commitWhenNoPopupOpen();
    },
    [commitWhenNoPopupOpen],
  );

  const onTileBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      const next = event.relatedTarget as Node | null;
      if (!next || !rootRef.current?.contains(next)) commitWhenNoPopupOpen();
    },
    [commitWhenNoPopupOpen],
  );

  const dateValue = useMemo(
    () => ({
      dateRange: misc?.dateRange ?? DATE_RANGE_DEFAULTS.dateRange,
      showAdvancedDate: misc?.showAdvancedDate ?? DATE_RANGE_DEFAULTS.showAdvancedDate,
      startDateType: misc?.startDateType ?? DATE_RANGE_DEFAULTS.startDateType,
      startDateLatestN: misc?.startDateLatestN ?? DATE_RANGE_DEFAULTS.startDateLatestN,
      startDateAbsolute: misc?.startDateAbsolute ?? DATE_RANGE_DEFAULTS.startDateAbsolute,
      startDateOffset: misc?.startDateOffset ?? DATE_RANGE_DEFAULTS.startDateOffset,
      endDateType: misc?.endDateType ?? DATE_RANGE_DEFAULTS.endDateType,
      endDateAbsolute: misc?.endDateAbsolute ?? DATE_RANGE_DEFAULTS.endDateAbsolute,
      endDateOffset: misc?.endDateOffset ?? DATE_RANGE_DEFAULTS.endDateOffset,
      dayFilter: misc?.dayFilter ?? DATE_RANGE_DEFAULTS.dayFilter,
    }),
    [misc],
  );

  const singleDateValue = useMemo(
    () => ({
      dateMode: misc?.dateMode ?? SINGLE_DATE_DEFAULTS.dateMode,
      dateOffset: misc?.dateOffset ?? SINGLE_DATE_DEFAULTS.dateOffset,
      fixedDate: misc?.fixedDate ?? SINGLE_DATE_DEFAULTS.fixedDate,
    }),
    [misc],
  );

  const subjectValue = useMemo(
    () => ({
      selectedSubjects: misc?.selectedSubjects ?? PLAYERS_DEFAULTS.selectedSubjects,
      selectedDrills: misc?.selectedDrills ?? PLAYERS_DEFAULTS.selectedDrills,
      subjectActiveTab: misc?.subjectActiveTab ?? PLAYERS_DEFAULTS.subjectActiveTab,
    }),
    [misc],
  );

  // Which face fits is a question about PIXELS, not grid units — so the tile measures
  // itself and flips live while the coach drags the resize handle. `null` until the
  // first measurement, so neither face flashes wrongly on mount.
  const [chipFace, setChipFace] = useState<boolean | null>(null);
  const [tallChip, setTallChip] = useState(false);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const measure = (width: number, height: number) => {
      setChipFace(width < INLINE_MIN_WIDTH_PX || height < INLINE_MIN_HEIGHT_PX);
      setTallChip(height >= TALL_CHIP_MIN_HEIGHT_PX);
    };
    measure(el.clientWidth, el.clientHeight);
    const ro = new ResizeObserver(([entry]) => {
      const r = entry?.contentRect;
      if (r) measure(r.width, r.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (!spec) return null;

  const controls: ControllerKind[] = spec.controls ?? [];
  const title = misc?.label || spec.display?.title || "Slicer";
  const summary = controls
    .map((kind) =>
      controllerSummary(kind, misc ?? {}, pipeline.getSubjectEntities, controls),
    )
    .join(" · ");
  // The 2-row chip face has a second text line, so it names the selection instead of
  // counting it.
  const summaryDetailed = controls
    .map((kind) =>
      controllerSummaryDetailed(kind, misc ?? {}, pipeline.getSubjectEntities, controls),
    )
    .join(" · ");

  // One tab per active picker: stacked pickers make the widget far too tall for a
  // dashboard tile. A controller with a single picker skips the tab bar entirely — there
  // is nothing to switch between, and the chrome would just cost vertical space. Only
  // legacy multi-kind controllers still see tabs.
  // `inPopover`: the popover has room, so the custom date range renders its full editor
  // inline instead of nesting a second chip-and-popover.
  const buildBody = (inPopover: boolean) => {
    const panes = [
      controls.includes("date") && {
        key: "date",
        label: "Dates",
        children: (
          <DateRangeForm
            value={dateValue}
            onChange={applyPatch}
            allowLatestN
            onPopupOpenChange={onPopupOpenChange}
            onSettle={commitWhenNoPopupOpen}
            description=""
            compact={!inPopover}
            size="middle"
          />
        ),
      },
      controls.includes("single_date") && {
        key: "single_date",
        label: "Day",
        children: (
          <SingleDateForm
            value={singleDateValue}
            onChange={applyPatch}
            onPopupOpenChange={onPopupOpenChange}
            onSettle={commitWhenNoPopupOpen}
            description=""
            size="middle"
          />
        ),
      },
      controls.includes("single_player") && {
        key: "single_player",
        label: "Athlete",
        children: (
          <SinglePlayerForm
            value={{ selectedPlayer: misc?.selectedPlayer ?? SINGLE_PLAYER_DEFAULTS.selectedPlayer }}
            onChange={applyPatch}
            onPopupOpenChange={onPopupOpenChange}
            getSubjectEntities={pipeline.getSubjectEntities}
            description=""
          />
        ),
      },
      controls.includes("subject") && {
        key: "subject",
        label: "Subjects",
        children: (
          <PlayersForm
            value={subjectValue}
            onChange={applyPatch}
            onPopupOpenChange={onPopupOpenChange}
            getSubjectEntities={pipeline.getSubjectEntities}
            description=""
          />
        ),
      },
    ].filter(Boolean) as { key: string; label: string; children: React.ReactNode }[];

    return (
      <div
        className="dynamic-controller-body"
        style={{ opacity: cannotEdit ? 0.6 : 1, pointerEvents: cannotEdit ? "none" : "auto" }}
      >
        {panes.length > 1 ? (
          <Tabs size="small" items={panes} className="dynamic-controller-tabs" />
        ) : (
          panes[0]?.children ?? null
        )}
      </div>
    );
  };

  const inlineFace = (
    <>
      <div className="dynamic-controller-header">
        {/* Inline line-height: antd's .ant-typography pins ~1.57, and the header must stay
            ~24px so two default-height selects fit a 3-row tile without a scrollbar. */}
        <Text strong style={{ fontSize: 13, lineHeight: 1.2 }}>{title}</Text>
      </div>
      {cannotEdit ? (
        <Tooltip title="You don't have permission to change this dashboard.">
          <div>{buildBody(false)}</div>
        </Tooltip>
      ) : (
        buildBody(false)
      )}
    </>
  );

  const chip = (
    <Popover
      content={
        // Dropdowns opened from inside the popover must mount INSIDE it — a popup
        // portaled to document.body sits outside the hover area, so opening one would
        // close the popover mid-edit.
        <ConfigProvider getPopupContainer={(node) => node?.parentElement ?? document.body}>
          <div className="dynamic-controller-popover">
            <Text strong style={{ fontSize: 12 }}>
              {title}
              {cannotEdit && (
                <Text type="secondary" style={{ fontSize: 11 }}> · view only</Text>
              )}
            </Text>
            {buildBody(true)}
          </div>
        </ConfigProvider>
      }
      trigger={["hover", "click"]}
      mouseEnterDelay={0.25}
      mouseLeaveDelay={0.4}
      placement="bottomLeft"
      onOpenChange={(open) => {
        if (open) {
          hover(controllerUuid);
          return;
        }
        unhover(controllerUuid);
        // The popover's content hides with its dropdowns still counted as open; a closed
        // popover is the end of the edit either way.
        openPopupsRef.current = 0;
        commit();
      }}
    >
      {tallChip ? (
        <div className="dynamic-controller-chip-face dynamic-controller-chip-face--tall">
          <span className="dynamic-controller-chip-title-row">
            <Text strong className="dynamic-controller-chip-title">{title}</Text>
            <EditOutlined className="dynamic-controller-chip-icon" />
          </span>
          <Text type="secondary" className="dynamic-controller-chip-summary">
            {summaryDetailed}
          </Text>
        </div>
      ) : (
        <div className="dynamic-controller-chip-face">
          <Text strong className="dynamic-controller-chip-title">{title}</Text>
          <Text type="secondary" className="dynamic-controller-chip-summary">{summary}</Text>
          <EditOutlined className="dynamic-controller-chip-icon" />
        </div>
      )}
    </Popover>
  );

  return (
    <div
      className="dynamic-controller-widget"
      ref={rootRef}
      // The tile's border, in a faint wash of this slicer's color — the full color while a
      // draft waits to commit. The gray wrapper border is blanked for slicer tiles in
      // CSS, so this ring IS the border.
      style={{ boxShadow: `inset 0 0 0 1.5px ${draft ? sliceColor(misc) : faintSliceColor(misc)}` }}
      onMouseEnter={() => hover(controllerUuid)}
      onMouseLeave={() => {
        unhover(controllerUuid);
        commitWhenNoPopupOpen();
      }}
      onBlur={onTileBlur}
    >
      {chipFace === null ? null : chipFace ? chip : inlineFace}
    </div>
  );
}
